import { Input, ALL_FORMATS, BlobSource } from "mediabunny"
import type { VideoMeta } from "./types"

/** Probe a File via the browser <video> element to get a reliable duration. */
function probeDurationViaVideo(file: File): Promise<number> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const vid = document.createElement("video")
    vid.preload = "metadata"
    vid.muted = true
    const cleanup = () => { URL.revokeObjectURL(url); vid.src = "" }
    vid.onloadedmetadata = () => {
      const d = vid.duration
      cleanup()
      resolve(Number.isFinite(d) && d > 0 ? d : 0)
    }
    vid.onerror = () => { cleanup(); resolve(0) }
    vid.src = url
  })
}

export async function probeFile(file: File): Promise<VideoMeta> {
  const input = new Input({ source: new BlobSource(file), formats: ALL_FORMATS })

  let format: string
  try {
    const detectedFormat = await input.getFormat()
    format = detectedFormat?.name || "unknown"
  } catch { format = "unknown" }

  let duration = 0
  try {
    const durationMicros = await input.computeDuration()
    duration = durationMicros / 1_000_000
  } catch { /* will fallback below */ }

  // Fallback: if mediabunny returned 0, NaN, or anything implausibly small
  // (some MP4 files have a missing/corrupt 'mvhd' duration atom but the
  // browser <video> element can still pull a real duration from the moof
  // boxes), use the video-element probe.
  //
  // Threshold of 1 second: no real video clip people upload to Commons is
  // shorter than a second, so anything below that is almost certainly a
  // demuxer artifact rather than a real duration.
  if (!Number.isFinite(duration) || duration < 1) {
    duration = await probeDurationViaVideo(file)
  }

  const videoTrack = await input.getPrimaryVideoTrack()
  if (!videoTrack) {
    throw new Error("This file has no video track. Only video files are supported.")
  }
  const audioTrack = await input.getPrimaryAudioTrack()
  const videoCodec = await videoTrack.getCodec()
  const audioCodec = audioTrack ? await audioTrack.getCodec() : ""

  let fps = 30
  try {
    const stats = await videoTrack.computePacketStats(50)
    if (stats?.averagePacketRate && stats.averagePacketRate > 0 && stats.averagePacketRate < 240) {
      fps = Math.round(stats.averagePacketRate)
    }
  } catch { /* */ }

  const displayWidth = await videoTrack.getDisplayWidth()
  const displayHeight = await videoTrack.getDisplayHeight()
  const rotation = await videoTrack.getRotation() || 0

  let creationDate: string | undefined
  try {
    const tags = await input.getMetadataTags()
    if (tags.date instanceof Date && isFinite(tags.date.getTime())) {
      const d = tags.date
      const yyyy = d.getFullYear()
      const mm = String(d.getMonth() + 1).padStart(2, "0")
      const dd = String(d.getDate()).padStart(2, "0")
      creationDate = `${yyyy}-${mm}-${dd}`
    }
  } catch { /* metadata is optional - never block the upload for this */ }

  return {
    width: displayWidth, height: displayHeight, duration, fps,
    rotation: rotation as 0 | 90 | 180 | 270,
    videoCodec: (videoCodec || "").toLowerCase(),
    audioCodec: (audioCodec || "").toLowerCase(),
    fileSize: file.size, containerFormat: format,
    creationDate
  }
}